import React                                  from "react"
import { useLocation }                        from "react-router-dom"
import { useGlobalConfig }                    from "context/GlobalContext"
import { Box, 
         Collapse, 
         List, 
         ListItem, 
         ListItemButton, 
         ListItemIcon, 
         ListItemText 
       }                                      from "@mui/material"
import { Home as LucideHome, ShieldCheck as LucideShieldCheck, ChevronDown as LucideChevronDown } from 'lucide-react';
import { ROUTE_TREE }                         from "routes/AppRoutes";
import { useCanAccessRoute }                  from "hooks/usePermissions";
import PollOutlinedIcon   from '@mui/icons-material/PollOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import AreaChartOutlinedIcon from '@mui/icons-material/AreaChartOutlined';
import { House } from 'lucide-react';
import { ShieldCheck } from 'lucide-react';
import { ChartArea } from 'lucide-react';
import { ChartColumn } from 'lucide-react';

export type UtilityDrawerProps = {
  isExpanded : boolean,
}

export const UtilityDrawer = ({
    isExpanded
  } : UtilityDrawerProps) => {

  const { rc } = useGlobalConfig()
  const location = useLocation()
  const canAccessRoute = useCanAccessRoute()

  const [adminOpen, setAdminOpen] = React.useState<boolean>(false)

  const showWorkflows              = canAccessRoute(ROUTE_TREE.WORKFLOW.LIST)
  const showEnvironments           = canAccessRoute(ROUTE_TREE.ENVIRONMENT.LIST)
  const showApplicationScopeMaster = canAccessRoute(ROUTE_TREE.APPLICATION_SCOPE_MASTER.LIST)
  const showGlobalDataScenarios    = canAccessRoute(ROUTE_TREE.GLOBAL_DATA_SCENARIOS.LIST)
  const showBrandGuidelines        = canAccessRoute(ROUTE_TREE.BRAND_GUIDELINES.LIST)
  const showPipelineMaster         = canAccessRoute(ROUTE_TREE.PIPELINE_MASTER.LIST)
  const showConfigRegistry         = canAccessRoute(ROUTE_TREE.CMS.CONFIG_REGISTRY)
  const showStringRegistry         = canAccessRoute(ROUTE_TREE.CMS.STRING_REGISTRY)
  const showRegistryAuditTrail     = canAccessRoute(ROUTE_TREE.CMS.AUDIT_TRAIL)
  const showRbac                   = canAccessRoute(ROUTE_TREE.RBAC.LIST)

  const hasVisibleAdminItems = showWorkflows || showEnvironments || showApplicationScopeMaster
    || showGlobalDataScenarios || showBrandGuidelines || showPipelineMaster
    || showConfigRegistry || showStringRegistry || showRegistryAuditTrail || showRbac

  const navToUtilityList = () => {
    rc.uiRouter.navigate(ROUTE_TREE.PROJECT.LIST, {
      disableBack : true
    })
  }

  const navToWorkflows = () => {
    rc.uiRouter.navigate(ROUTE_TREE.WORKFLOW.LIST)
  }

  const navToEnvironments = () => {
    rc.uiRouter.navigate(ROUTE_TREE.ENVIRONMENT.LIST)
  }

  const navToApplicationScopeMaster = () => {
    rc.uiRouter.navigate(ROUTE_TREE.APPLICATION_SCOPE_MASTER.LIST)
  }

  const navToPipelineMaster = () => {
    rc.uiRouter.navigate(ROUTE_TREE.PIPELINE_MASTER.LIST)
  }

  const navToGlobalDataScenarios = () => {
    rc.uiRouter.navigate(ROUTE_TREE.GLOBAL_DATA_SCENARIOS.LAYOUT)
  }

  const navToBrandGuidelines = () => {
    rc.uiRouter.navigate(ROUTE_TREE.BRAND_GUIDELINES.LIST)
  }

  const navToConfigRegistry = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CMS.CONFIG_REGISTRY)
  }

  const navToStringRegistry = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CMS.STRING_REGISTRY)
  }

  const navToRbac = () => {
    rc.uiRouter.navigate(ROUTE_TREE.RBAC.LIST)
  }

  const navToRegistryAuditTrail = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CMS.AUDIT_TRAIL)
  }

  const isActive = (pathPrefix: string) => location.pathname.startsWith(pathPrefix)


  const isAdminRoute = isActive('/workflow') || isActive('/environment') || isActive('/application-scope-master') || isActive('/pipeline-master') || isActive('/global-data-scenarios') || isActive('/brand-guidelines') || isActive('/cms/config-registry') || isActive('/cms/string-registry') || isActive('/rbac') || isActive('/cms/audit-trail');

  React.useEffect(() => {
    if (isAdminRoute) {
      setAdminOpen(true)
    }
  }, [location.pathname])

  return (
    <React.Fragment>
      <ListItem disablePadding>
        <ListItemButton 
          onClick={navToUtilityList} 
          selected={isActive('/project/list')}
          sx={!isExpanded ? {
            display: 'flex',
            justifyContent: 'center',
            padding: '8px !important'
          } : {}}
        >
          <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' }}>
            <House style={{ color: '#bfccd9' }} size={16} />
          </ListItemIcon>
          {isExpanded && <ListItemText primary="Projects" primaryTypographyProps={{ sx: { fontWeight: 500, fontSize: 14, color: 'inherit' } }} />}
        </ListItemButton>
      </ListItem>

      {/* Administration group as accordion header — hidden when every child is denied */}
      {hasVisibleAdminItems && (
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setAdminOpen(v => !v)}
            selected={adminOpen || isAdminRoute}
            sx={!isExpanded ? {
              display: 'flex',
              justifyContent: 'center',
              padding: '8px !important'
            } : { gap: 1 }}
          >
            <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' }}>
              <ShieldCheck style={{ color: '#bfccd9' }} size={18} />
            </ListItemIcon>
            {isExpanded && <ListItemText primary="Administration" primaryTypographyProps={{ sx: { color: 'inherit', fontWeight: 600, fontSize: 14 } }} />}
            {isExpanded && (
              <LucideChevronDown size={18} style={{ marginLeft: 'auto', transform: adminOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
            )}
          </ListItemButton>
        </ListItem>
      )}
      {hasVisibleAdminItems && (
        <Collapse in={adminOpen && isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ ml: '26px', borderLeft: 2, borderColor: 'divider', pl: 1.75, my: 0.5 }}>
            <List component="div" disablePadding>
              {showWorkflows && (
                <ListItemButton
                  dense
                  sx={{
                    py: 0.75,
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&.Mui-selected': {
                      color: '#e8eaf6',
                    }
                  }}
                  onClick={navToWorkflows}
                  selected={isActive('/workflow')}
                >
                  <ListItemText
                    primary="Workflows"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: isActive('/workflow') ? 600 : 500,
                        color: 'inherit'
                      }
                    }}
                  />
                </ListItemButton>
              )}
              {showEnvironments && (
                <ListItemButton
                  dense
                  sx={{
                    py: 0.75,
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&.Mui-selected': {
                      color: '#e8eaf6',
                    }
                  }}
                  onClick={navToEnvironments}
                  selected={isActive('/environment')}
                >
                  <ListItemText
                    primary="Environments"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: isActive('/environment') ? 600 : 500,
                        color: 'inherit'
                      }
                    }}
                  />
                </ListItemButton>
              )}
              {showApplicationScopeMaster && (
                <ListItemButton
                  dense
                  sx={{
                    py: 0.75,
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&.Mui-selected': {
                      color: '#e8eaf6',
                    }
                  }}
                  onClick={navToApplicationScopeMaster}
                  selected={isActive('/application-scope-master')}
                >
                  <ListItemText
                    primary="Application Scope"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: isActive('/application-scope-master') ? 600 : 500,
                        color: 'inherit'
                      }
                    }}
                  />
                </ListItemButton>
              )}
              {showGlobalDataScenarios && (
                <ListItemButton
                  dense
                  sx={{
                    py: 0.75,
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&.Mui-selected': {
                      color: '#e8eaf6',
                    }
                  }}
                  onClick={navToGlobalDataScenarios}
                  selected={isActive('/global-data-scenarios')}
                >
                  <ListItemText
                    primary="Global Data Scenarios"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: isActive('/global-data-scenarios') ? 600 : 500,
                        color: 'inherit'
                      }
                    }}
                  />
                </ListItemButton>
              )}
              {showBrandGuidelines && (
                <ListItemButton
                  dense
                  sx={{
                    py: 0.75,
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&.Mui-selected': {
                      color: '#e8eaf6',
                    }
                  }}
                  onClick={navToBrandGuidelines}
                  selected={isActive('/brand-guidelines')}
                >
                  <ListItemText
                    primary="Brand Guidelines"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: isActive('/brand-guidelines') ? 600 : 500,
                        color: 'inherit'
                      }
                    }}
                  />
                </ListItemButton>
              )}
              {showPipelineMaster && (
                <ListItemButton
                  dense
                  sx={{
                    py: 0.75,
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&.Mui-selected': {
                      color: '#e8eaf6',
                    }
                  }}
                  onClick={navToPipelineMaster}
                  selected={isActive('/pipeline-master')}
                >
                  <ListItemText
                    primary="Pipeline Master"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: isActive('/pipeline-master') ? 600 : 500,
                        color: 'inherit'
                      }
                    }}
                  />
                </ListItemButton>
              )}
              {showConfigRegistry && (
                <ListItemButton
                  dense
                  sx={{
                    py: 0.75,
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&.Mui-selected': {
                      color: '#e8eaf6',
                    }
                  }}
                  onClick={navToConfigRegistry}
                  selected={isActive('/cms/config-registry')}
                >
                  <ListItemText
                    primary="Config Registry"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: isActive('/cms/config-registry') ? 600 : 500,
                        color: 'inherit'
                      }
                    }}
                  />
                </ListItemButton>
              )}
              {showStringRegistry && (
                <ListItemButton
                  dense
                  sx={{
                    py: 0.75,
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&.Mui-selected': {
                      color: '#e8eaf6',
                    }
                  }}
                  onClick={navToStringRegistry}
                  selected={isActive('/cms/string-registry')}
                >
                  <ListItemText
                    primary="String Registry"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: isActive('/cms/string-registry') ? 600 : 500,
                        color: 'inherit'
                      }
                    }}
                  />
                </ListItemButton>
              )}
              {showRegistryAuditTrail && (
                <ListItemButton
                  dense
                  sx={{
                    py: 0.75,
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&.Mui-selected': {
                      color: '#e8eaf6',
                    }
                  }}
                  onClick={navToRegistryAuditTrail}
                  selected={isActive('/cms/audit-trail')}
                >
                  <ListItemText
                    primary="Registry Audit Trail"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: isActive('/cms/audit-trail') ? 600 : 500,
                        color: 'inherit'
                      }
                    }}
                  />
                </ListItemButton>
              )}
              {showRbac && (
                <ListItemButton
                  dense
                  sx={{
                    py: 0.75,
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&.Mui-selected': {
                      color: '#e8eaf6',
                    }
                  }}
                  onClick={navToRbac}
                  selected={isActive('/rbac')}
                >
                  <ListItemText
                    primary="Access Control"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: isActive('/rbac') ? 600 : 500,
                        color: 'inherit'
                      }
                    }}
                  />
                </ListItemButton>
              )}

            </List>
          </Box>
        </Collapse>
      )}

      <ListItem disablePadding>
        <ListItemButton 
          onClick={navToWorkflows} 
          sx={!isExpanded ? {
            display: 'flex',
            justifyContent: 'center',
            padding: '8px !important'
          } : { gap: 1 }}
        >
          <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto', color: '#bfccd9' }}>
            <ChartArea style={{ color: '#bfccd9' }} size={18} />
          </ListItemIcon>
          {isExpanded && <ListItemText primary="System Monitoring" primaryTypographyProps={{ sx: { color: '#bfccd9',fontSize: '14px' } }} />}
        </ListItemButton>
      </ListItem>

      <ListItem disablePadding>
        <ListItemButton 
          sx={!isExpanded ? {
            display: 'flex',
            justifyContent: 'center',
            padding: '8px !important'
          } : { gap: 1 }}
        >
          <ListItemIcon sx={{ minWidth: isExpanded ? '30px' : 'auto' ,color: '#bfccd9' }}>
            <ChartColumn style={{ color: '#bfccd9' }} size={18} />
          </ListItemIcon>
          {isExpanded && <ListItemText primary="System KPIs" primaryTypographyProps={{ sx: { color: '#bfccd9',fontSize: '14px'} }} />}
        </ListItemButton>
      </ListItem>

    </React.Fragment>
  )
}